import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProfileCodeChip } from "@/components/ProfileCodeChip";
import { BUSINESS_TYPE_LABELS } from "@/lib/constants";
import { useOrg } from "@/components/org/useOrg";
import { DepartmentPicker, RolePicker } from "@/components/org/DepartmentPicker";
import { EmployeesTab } from "@/components/org/EmployeesTab";
import { PermissionsTab } from "@/components/org/OrgTabs";
import { ArrowRight, ArrowLeft, Check, Sparkles, Send, PartyPopper } from "lucide-react";

const STEPS = ["Welcome", "Departments", "Roles", "Employees", "Permissions", "Partners", "Done"];
const LAST = STEPS.length - 1;

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2" data-testid="setup-stepper">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-[#0D5C3A] text-white" : i === step ? "bg-[#C85A32] text-white" : "bg-[#EDE8DF] text-stone-500"}`}>{i < step ? <Check className="h-4 w-4" /> : i + 1}</div>
          <span className={`hidden text-xs font-semibold md:inline ${i === step ? "text-stone-900" : "text-stone-400"}`}>{s}</span>
          {i < STEPS.length - 1 && <div className="h-px flex-1 bg-[#E5E0D8]" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function PartnerInvite() {
  const { business } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/partners/search", { code });
      await api.post("/partners", { partner_business_id: r.data.business.id });
      toast.success(`Connect request sent to ${r.data.business.name}`); setCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-[#E5E0D8] bg-[#FAF8F5] p-5">
        <p className="text-sm text-stone-600">Share your profile code so other businesses can send you a connect request:</p>
        <div className="mt-2"><ProfileCodeChip code={business?.profile_code} /></div>
      </div>
      <div>
        <p className="text-sm font-semibold text-stone-800">Send a connect request</p>
        <p className="text-xs text-stone-500">They'll need to accept before you can trade. Partners are external businesses, not employees.</p>
        <div className="mt-2 flex gap-2">
          <Input placeholder="RV-MFR-10001" value={code} onChange={(e) => setCode(e.target.value)} data-testid="setup-partner-code-input" />
          <Button disabled={busy || !code.trim()} onClick={send} data-testid="setup-partner-send-btn" className="bg-[#C85A32] hover:bg-[#a8481f]"><Send className="mr-1 h-4 w-4" /> Send</Button>
        </div>
      </div>
    </div>
  );
}

export default function OrgSetup() {
  const { user, business, refresh } = useAuth();
  const navigate = useNavigate();
  const { data, reload } = useOrg(true);
  const [step, setStep] = useState(null);
  const [deps, setDeps] = useState([]);
  const [roles, setRoles] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!data || step !== null) return;
    setStep(data.setup.status === "completed" ? 0 : Math.min(data.setup.step || 0, LAST - 1));
    setDeps(data.setup.departments);
    setRoles(data.setup.roles);
  }, [data, step]);

  if (user?.role !== "owner") return <p className="text-stone-500" data-testid="setup-no-access">Only the Super Admin can run organisation setup.</p>;
  if (!data || step === null) return <Loader />;

  const save = async (body) => {
    setBusy(true);
    try { await api.put("/org/setup", body); return true; } catch (e) { toast.error(e.response?.data?.detail || "Failed"); return false; } finally { setBusy(false); }
  };
  const go = async (next, body = {}) => { if (await save({ ...body, step: next, status: next >= LAST ? "completed" : "in_progress" })) { setStep(next); reload(); if (next >= LAST) refresh(); } };
  const later = async () => { if (await save({ status: "skipped", step })) { await refresh(); navigate("/app"); } };

  const next = () => {
    if (step === 1) go(2, { departments: deps });
    else if (step === 2) go(3, { roles: Object.fromEntries(Object.entries(roles).filter(([k]) => !deps.length || deps.includes(k))) });
    else go(step + 1);
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="org-setup-page">
      <Stepper step={step} />
      <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="rounded-3xl border border-[#E5E0D8] bg-white p-6 shadow-sm sm:p-8">
        {step === 0 && (
          <div data-testid="setup-welcome">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F0ECE1] text-[#0D5C3A]"><Sparkles className="h-7 w-7" /></div>
            <h1 className="mt-4 font-heading text-3xl font-extrabold text-stone-900">Welcome, {business?.owner_name}!</h1>
            <p className="mt-2 max-w-xl text-stone-500">Let's set up <b>{business?.name}</b> as a {BUSINESS_TYPE_LABELS[business?.business_type]}. Pick your departments, choose the roles you use, add your team and connect with partners. You're the Super Admin — you can change everything later.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" disabled={busy} onClick={() => go(1)} data-testid="setup-start-btn" className="bg-[#0D5C3A] hover:bg-[#084229]">Start Setup <ArrowRight className="ml-2 h-4 w-4" /></Button>
              <Button size="lg" variant="outline" disabled={busy} onClick={later} data-testid="setup-later-btn">Skip for now</Button>
            </div>
          </div>
        )}
        {step === 1 && (<div className="space-y-4"><h2 className="font-heading text-xl font-bold">Which departments does your business have?</h2><p className="text-sm text-stone-500">Suggested for a {BUSINESS_TYPE_LABELS[business?.business_type]}. Untick the ones you don't need.</p><DepartmentPicker catalog={data.catalog} selected={deps} onChange={setDeps} onReload={reload} /></div>)}
        {step === 2 && (<div className="space-y-4"><h2 className="font-heading text-xl font-bold">Which roles do you use in each department?</h2><RolePicker catalog={data.catalog} departments={deps} roles={roles} onChange={setRoles} onReload={reload} /></div>)}
        {step === 3 && (<div className="space-y-4"><h2 className="font-heading text-xl font-bold">Add your team</h2><p className="text-sm text-stone-500">Each employee signs in with their own mobile number and the password you set. You can skip this and add people later.</p><EmployeesTab data={data} reload={reload} compact /></div>)}
        {step === 4 && (<div className="space-y-4"><h2 className="font-heading text-xl font-bold">Permissions & access control</h2><p className="text-sm text-stone-500">Choose exactly what each employee can view, create, edit, approve or delete. Job titles don't grant access on their own.</p><PermissionsTab data={data} reload={reload} /></div>)}
        {step === 5 && (<div className="space-y-4"><h2 className="font-heading text-xl font-bold">Connect with business partners</h2><PartnerInvite /></div>)}
        {step === LAST && (
          <div className="text-center" data-testid="setup-done">
            <PartyPopper className="mx-auto h-12 w-12 text-[#C85A32]" />
            <h2 className="mt-3 font-heading text-2xl font-extrabold">Your organisation is ready</h2>
            <p className="mt-1 text-stone-500">{deps.length || data.catalog.length} departments · {data.employees.length} employees</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button onClick={() => navigate("/app")} data-testid="setup-goto-dashboard-btn" className="bg-[#0D5C3A] hover:bg-[#084229]">Go to dashboard</Button>
              <Button variant="outline" onClick={() => navigate("/app/organization")} data-testid="setup-goto-org-btn">Manage organisation</Button>
            </div>
          </div>
        )}
        {step >= 1 && step < LAST && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-[#F0ECE1] pt-5">
            <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="setup-back-btn"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
            <div className="flex gap-2">
              <Button variant="outline" disabled={busy} onClick={later} data-testid="setup-save-later-btn">Save & finish later</Button>
              <Button variant="ghost" disabled={busy} onClick={() => go(step + 1)} data-testid="setup-skip-btn">Skip for now</Button>
              <Button disabled={busy} onClick={next} data-testid="setup-next-btn" className="bg-[#0D5C3A] hover:bg-[#084229]">{step === LAST - 1 ? "Finish" : "Save & Continue"} <ArrowRight className="ml-1 h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
